// ─────────────────────────────────────────────────────────────────────────────
// NetworkCard — VPS / Socket.IO network stats for the desktop homepage right panel
// (Chunk 5). Reads the app-wide singleton (getRexySocket) and reports:
//   • VPS connection status + host (herexy.com in prod, localhost:5000 in dev —
//     matches the useRexySocket origin)
//   • active Socket.IO transport type
//   • packets in / out (engine.io 'packet' / 'packetCreate' counters)
//   • connection uptime since the last connect
//
// Packet counters live in refs and are flushed to state on a 1 Hz tick (the same
// tick that recomputes uptime) so per-packet traffic never thrashes React. Engine
// events aren't in the public typings → accessed via a narrow `any`.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useRef, useState } from 'react';
import { REXY_CONSOLE } from '../lib/rexyConsole';
import { getRexySocket } from '../hooks/useRexySocket';

const MONO = "'Courier New', monospace";

// Host only (no scheme) — mirrors useRexySocket's origin selection.
const VPS_HOST = process.env.NODE_ENV === 'production' ? 'herexy.com' : 'localhost:5000';

function fmtUptime(ms: number | null): string {
  if (ms == null) return '—';
  const s = Math.floor(ms / 1000);
  const hh = String(Math.floor(s / 3600)).padStart(2, '0');
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

function StatRow({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={styles.row}>
      <span style={styles.label}>{label}</span>
      <span style={{ ...styles.value, color: color ?? REXY_CONSOLE.primary }}>{value}</span>
    </div>
  );
}

export default function NetworkCard() {
  const [connected, setConnected] = useState(false);
  const [transport, setTransport] = useState<string | null>(null);
  const [packetsIn, setPacketsIn] = useState(0);
  const [packetsOut, setPacketsOut] = useState(0);
  const [uptime, setUptime] = useState<string>('—');

  const inRef = useRef(0);
  const outRef = useRef(0);
  const connectedSinceRef = useRef<number | null>(null);

  useEffect(() => {
    const socket = getRexySocket();
    const mgr = socket.io;
    let engine: any = null;

    const syncTransport = () => {
      const e: any = mgr.engine;
      setTransport(e?.transport?.name ?? null);
    };
    const onPacketIn = () => {
      inRef.current += 1;
    };
    const onPacketOut = () => {
      outRef.current += 1;
    };

    const bindEngine = () => {
      engine = mgr.engine as any;
      if (!engine) return;
      syncTransport();
      engine.on('packet', onPacketIn); // inbound engine packet
      engine.on('packetCreate', onPacketOut); // outbound engine packet
      engine.on('upgrade', syncTransport);
    };
    const unbindEngine = () => {
      if (!engine) return;
      engine.off('packet', onPacketIn);
      engine.off('packetCreate', onPacketOut);
      engine.off('upgrade', syncTransport);
      engine = null;
    };

    const onConnect = () => {
      setConnected(true);
      connectedSinceRef.current = Date.now();
      unbindEngine();
      bindEngine();
    };
    const onDisconnect = () => {
      setConnected(false);
      connectedSinceRef.current = null;
    };

    if (socket.connected) {
      setConnected(true);
      connectedSinceRef.current = Date.now();
      bindEngine();
    }

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);

    // 1 Hz tick — flush packet counters + recompute uptime (no per-packet re-render).
    const tick = window.setInterval(() => {
      setPacketsIn(inRef.current);
      setPacketsOut(outRef.current);
      const since = connectedSinceRef.current;
      setUptime(since == null ? '—' : fmtUptime(Date.now() - since));
    }, 1000);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      unbindEngine();
      window.clearInterval(tick);
    };
  }, []);

  return (
    <div style={styles.card}>
      <div style={styles.sectionHeader}>║ NETWORK</div>
      <StatRow
        label="VPS"
        value={connected ? `ONLINE · ${VPS_HOST}` : `OFFLINE · ${VPS_HOST}`}
        color={connected ? REXY_CONSOLE.primary : REXY_CONSOLE.critical}
      />
      <StatRow label="TRANSPORT" value={transport ?? 'N/A'} />
      <StatRow label="PACKETS IN" value={String(packetsIn)} color={REXY_CONSOLE.dimText} />
      <StatRow label="PACKETS OUT" value={String(packetsOut)} color={REXY_CONSOLE.dimText} />
      <StatRow label="UPTIME" value={uptime} />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    border: `1px solid ${REXY_CONSOLE.cardBorder}`,
    background: REXY_CONSOLE.cardBg,
    borderRadius: 6,
    padding: 14,
    boxShadow: '0 4px 14px rgba(0,0,0,0.45), inset 0 1px 0 rgba(0,191,255,0.06)',
  },
  sectionHeader: {
    fontFamily: MONO,
    fontSize: 12,
    letterSpacing: 2,
    color: REXY_CONSOLE.primary,
    marginBottom: 10,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '3px 0',
  },
  label: {
    fontFamily: MONO,
    fontSize: 11,
    letterSpacing: 1.5,
    color: REXY_CONSOLE.primary,
  },
  value: {
    fontFamily: MONO,
    fontSize: 11,
    textAlign: 'right',
  },
};
